import { makeStyles, Button, Container, Grid, Typography, Theme, createStyles, Hidden } from '@material-ui/core';
import { ActivePane, useAppStateContext } from '../../AppStateProvider/AppStateProvider';
import { CheckMark } from '../../../icons/CheckMark';
import { SmallError } from '../../../icons/SmallError';
import { InfoIcon } from '../../../icons/InfoIcon';
import SomeFailed from './SomeFailed.png';
import TestsPassed from './TestsPassed.png';
import GeneratePDFButton from './GeneratePDFButton';
import { getConnectivityStatus } from '../../../utils';
import { PreflightTest } from '@twilio/voice-sdk';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      float: 'left',
      [theme.breakpoints.down('md')]: {
        float: 'initial',
        paddingBottom: '1em',
      },
    },
    buttonContainer: {
      display: 'inline-flex',
      flexWrap: 'wrap',
      gap: '1em',
      width: '100%',
    },
    resultsList: {
      float: 'right',
      [theme.breakpoints.down('md')]: {
        float: 'initial',
      },
    },
    resultContainer: {
      marginTop: '1.5em',
      '&:not(:last-child)': {
        paddingBottom: '1.5em',
        borderBottom: '0.1em solid #8891AA',
      },
      [theme.breakpoints.down('md')]: {
        '&:last-child': {
          paddingBottom: '1.5em',
        },
      },
    },
    iconContainer: {
      display: 'flex',
      '& svg': {
        margin: '0.2em 0.8em 0 0',
      },
    },
    downloadButton: {
      '& svg': {
        position: 'relative',
        left: '-5px',
      },
    },
    restartButton: {
      backgroundColor: '#FFFFFF',
      borderColor: '#8891AA',
    },
    illustration: {
      marginTop: '7em',
    },
    hardwareButton: {
      marginRight: '1.5em',
    },
    gutterBottom: {
      marginBottom: '1em',
      [theme.breakpoints.down('md')]: {
        marginBottom: '1.5em',
      },
    },
  })
);

export function Results() {
  const { state, dispatch } = useAppStateContext();
  const classes = useStyles();

  const connectivityStatus = getConnectivityStatus(state.networkTestResult, state.conferencingStatusError);
  const qualityScore = state.networkTestResult?.results.preflight?.callQuality;
  const qualityTestsPassed = qualityScore === 'great' || qualityScore === 'excellent' || qualityScore === 'good';

  return (
    <>
      <Container>
        <div>
          <Grid item lg={5} className={classes.header}>
            <Typography variant="h1" gutterBottom>
              All checks complete!
            </Typography>

            {qualityScore ? (
              qualityTestsPassed ? (
                <Typography variant="body1" gutterBottom>
                  As far as we can tell, your device should be working. If you're still experiencing issues, download
                  your report results and send to your network administrator.
                </Typography>
              ) : (
                <Typography variant="body1" gutterBottom>
                  <strong>One or more </strong>
                  tests failed - use this list to solve common issues and restart the test. If you can't easily solve
                  the problem(s), download your report results and send to your network administrator.
                </Typography>
              )
            ) : (
              <Typography variant="body1" gutterBottom>
                Your user details, audio-device checks, and browser compatibility check are complete. Download your
                report for your records.
              </Typography>
            )}

            <div className={classes.buttonContainer}>
              {/* <Button
                variant="contained"
                color="primary"
                className={classes.downloadButton}
                onClick={finalResultsMinified}
              >
                <DownloadIcon />
                Download report results
              </Button> */}
              <GeneratePDFButton />
              <Button variant="outlined" className={classes.restartButton} onClick={() => window.location.reload()}>
                Restart test
              </Button>
            </div>

            <Hidden mdDown>
              <img
                src={qualityTestsPassed ? TestsPassed : SomeFailed}
                alt={qualityTestsPassed ? 'Success' : 'Some Failed'}
                className={classes.illustration}
              />
            </Hidden>
          </Grid>

          <Grid item lg={5} className={classes.resultsList}>
            <div className={classes.resultContainer}>
              <div className={classes.iconContainer}>
                <CheckMark />
                <Typography variant="h3" className={classes.gutterBottom}>
                  User Details
                </Typography>
              </div>
              <Typography variant="body1" className={classes.gutterBottom}>
                Email Address: <strong>{state?.userInfo?.email}</strong>
              </Typography>

              <div className={classes.buttonContainer}>
                <Button
                  variant="outlined"
                  onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.UserInfo })}
                >
                  Review details
                </Button>
              </div>
            </div>
            <div className={classes.resultContainer}>
              <div className={classes.iconContainer}>
                <CheckMark />
                <Typography variant="h3" className={classes.gutterBottom}>
                  Device &amp; Network Setup
                </Typography>
              </div>
              <Typography variant="body1" className={classes.gutterBottom}>
                Your audio-device checks and browser compatibility check are complete.
              </Typography>

              <div className={classes.buttonContainer}>
                <Button
                  variant="outlined"
                  onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.AudioTest })}
                >
                  Review hardware
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.BrowserTest })}
                >
                  Review browser
                </Button>
              </div>
            </div>

            <div className={classes.resultContainer}>
              <div className={classes.iconContainer}>
                {!connectivityStatus.hasRun ? <InfoIcon /> : connectivityStatus.passed ? <CheckMark /> : <SmallError />}
                <Typography variant="h3" className={classes.gutterBottom}>
                  Connectivity
                </Typography>
              </div>
              {connectivityStatus.hasRun ? (
                <>
                  <Typography variant="body1" className={classes.gutterBottom}>
                    {connectivityStatus.passed
                      ? 'All connections are working successfully.'
                      : 'One or more connectivity checks failed. Review the details below.'}
                  </Typography>
                </>
              ) : (
                <Typography variant="body1" className={classes.gutterBottom}>
                  Connectivity check has not been run yet.
                </Typography>
              )}
              <Button
                variant="outlined"
                onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.Connectivity })}
              >
                Review connectivity
              </Button>
            </div>

            {qualityScore && (
              <div className={classes.resultContainer}>
                <div className={classes.iconContainer}>
                  {qualityTestsPassed ? <CheckMark /> : <SmallError />}
                  <Typography variant="h3" className={classes.gutterBottom}>
                    Quality &amp; Performance
                  </Typography>
                </div>

                {qualityTestsPassed ? (
                  <Typography variant="body1" className={classes.gutterBottom}>
                    Awesome! Your expected call quality is <strong>{qualityScore}</strong> and overall performance looks
                    {qualityScore === PreflightTest.CallQuality.Excellent ? ' good' : ' ok'}.
                  </Typography>
                ) : (
                  <Typography variant="body1" className={classes.gutterBottom}>
                    Your overall score is <strong>{qualityScore}</strong> which means that your connection isn't good
                    enough to run audio calls properly. Try out these tips and rerun the test.
                  </Typography>
                )}

                <Button
                  variant="outlined"
                  onClick={() => dispatch({ type: 'set-active-pane', newActivePane: ActivePane.Quality })}
                >
                  Review performance
                </Button>
              </div>
            )}
          </Grid>
        </div>
      </Container>
    </>
  );
}
